import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { TextInput, Accordion, Button } from '@/components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import Stepper from '@/components/ui/Stepper/Stepper';
import type { StepConfig } from '@/components/ui/Stepper/types';
import StepperModalFooter from '@/components/shared/StepperModalFooter/StepperModalFooter';
import MandatoryFormRenderer from '@components/shared/MandatoryFormRenderer/MandatoryFormRenderer';
import useMultiStepForm from '@/hooks/useMultiStepForm';
import { useTranslation } from '@/hooks/useTranslation';
import { createTenant, updateTenant, getProductTypes, resolveCountries, checkSubdomain } from '@/services/tenantService';
import { slugifyToSubdomain } from './subdomainEngine';
import { useCountries } from '@/hooks/useCountries';
import { getFormTemplateByScope, filterFormTemplate } from '@/services/formTemplateService';
import { getModules } from '@/services/moduleService';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { FormTemplateTab } from '@/types/formTemplate';
import type { ModuleDto } from '@/types/module';
import type {
  CreateTenantPayload,
  ProductTypeCountryResolution,
  TenantWithUser,
  UpdateTenantPayload,
} from '@/types/tenant';
import styles from './CreateTenantModal.module.scss';
import FormField from '@/components/ui/Input/FormField';

// ============================================
// TYPES
// ============================================

interface TenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: TenantWithUser | null;
}

interface TenantFormValues {
  organizationName: string;
  subdomain: string;
  organizationSubType: string;
  country: string;
  mobile: string;
  productTypes: string[];
  firstName: string;
  lastName: string;
  email: string;
}

const STEP_IDS = ['company', 'mandatory', 'templates'] as const;

const STEP_FIELDS: Record<number, (keyof TenantFormValues)[]> = {
  0: ['organizationName', 'organizationSubType', 'country', 'mobile', 'productTypes', 'firstName', 'lastName', 'email'],
  1: [],
  2: [],
};

const fetchProductTypes = () => getProductTypes({ size: 100 });
const fetchEmploymentTypes = () => getEmploymentTypes({ size: 100 });

/** A single commercial term row in the Step 2 form */
interface CommercialTermRow {
  _id: string; // client-side tracking
  productTypeId: string;
  countryId: string;
  employmentTypeId: string;
  employeeCount: string;
}

let ctRowCounter = 0;
function nextCtRowId(): string {
  ctRowCounter += 1;
  return `ct_${ctRowCounter}`;
}

// Organization sub type option values (labels translated inside component)
const ORG_SUB_TYPE_VALUES = ['STANDARD_ACCOUNT', 'GROUP_ACCOUNT', 'PARTNER_ACCOUNT'] as const;

const DEFAULT_FORM_VALUES: TenantFormValues = {
  organizationName: '',
  subdomain: '',
  organizationSubType: '',
  country: '',
  mobile: '',
  productTypes: [],
  firstName: '',
  lastName: '',
  email: '',
};

// ============================================
// COMPONENT
// ============================================

function TenantModal({ isOpen, onClose, editData }: TenantModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const session = useAuthStore((s) => s.session);
  const orgType = session?.organization.type ?? '';
  const orgId = session?.organization.id ?? '';
  const isEditMode = Boolean(editData);

  const STEPS: StepConfig[] = useMemo(() => [
    { id: STEP_IDS[0], label: t('K_STEP_COMPANY_DETAILS', 'Company Details') },
    { id: STEP_IDS[1], label: t('K_STEP_MANDATORY_FORMS', 'Mandatory Forms') },
    { id: STEP_IDS[2], label: t('K_STEP_REQUEST_DATA', 'Request Data') },
  ], [t]);

  const ORGANIZATION_SUB_TYPE_OPTIONS = useMemo(() => [
    { label: t('K_OPTION_STANDARD_ACCOUNT', 'Standard Account'), value: ORG_SUB_TYPE_VALUES[0] },
    { label: t('K_OPTION_GROUP_ACCOUNT', 'Group Account'), value: ORG_SUB_TYPE_VALUES[1] },
    { label: t('K_OPTION_PARTNER_ACCOUNT', 'Partner Account'), value: ORG_SUB_TYPE_VALUES[2] },
  ], [t]);

  // All resolved tabs from single filter API call (no attributes filter)
  const [resolvedTabs, setResolvedTabs] = useState<FormTemplateTab[]>([]);
  // Form data collected from mandatory Formio forms (Step 2)
  const [formDataMap, setFormDataMap] = useState<Record<string, Record<string, unknown>>>({});
  // Tracks whether each form has required Formio fields (for smart button blocking)
  const [requiredFieldsMap, setRequiredFieldsMap] = useState<Record<string, boolean>>({});

  // Selected optional module IDs for Step 2 module registry
  const [selectedModuleIds, setSelectedModuleIds] = useState<Set<string>>(new Set());

  // Commercial terms rows for Step 2
  const [commercialTermRows, setCommercialTermRows] = useState<CommercialTermRow[]>([]);
  const [commercialTermErrors, setCommercialTermErrors] = useState<string | null>(null);

  // Full country resolution per product type (from POST /v1/countries/resolve)
  const [countryResolutions, setCountryResolutions] = useState<ProductTypeCountryResolution[]>([]);

  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  const { countries, isLoading: countriesLoading } = useCountries();

  const { data: productTypesData } = useQuery({
    queryKey: ['productTypes'],
    queryFn: fetchProductTypes,
  });

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes'],
    queryFn: fetchEmploymentTypes,
  });

  const employmentTypeOptions = useMemo(
    () => (employmentTypesData?.content ?? []).map((et) => ({ label: et.name, value: et.id })),
    [employmentTypesData],
  );

  const { data: modulesData } = useQuery({
    queryKey: ['modules', 'TENANT'],
    queryFn: () => getModules({ size: 100 }, { filters: { visibility: 'TENANT' } }),
    enabled: isOpen,
  });

  const defaultModules = useMemo(
    () => (modulesData?.content ?? []).filter((m: ModuleDto) => m.isDefault),
    [modulesData],
  );

  const optionalModules = useMemo(
    () => (modulesData?.content ?? []).filter((m: ModuleDto) => !m.isDefault),
    [modulesData],
  );

  const toggleModule = useCallback((moduleId: string) => {
    setSelectedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  // --- Commercial term row handlers ---
  const addCommercialTermRow = useCallback(() => {
    setCommercialTermRows((prev) => [
      ...prev,
      { _id: nextCtRowId(), productTypeId: '', countryId: '', employmentTypeId: '', employeeCount: '' },
    ]);
    setCommercialTermErrors(null);
  }, []);

  const removeCommercialTermRow = useCallback((id: string) => {
    setCommercialTermRows((prev) => prev.filter((r) => r._id !== id));
    setCommercialTermErrors(null);
  }, []);

  const updateCommercialTermRow = useCallback((id: string, patch: Partial<CommercialTermRow>) => {
    setCommercialTermRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, ...patch } : r)),
    );
    setCommercialTermErrors(null);
  }, []);

  /** Returns error string or null if valid */
  const validateCommercialTerms = useCallback((rows: CommercialTermRow[], selectedProductTypes: string[]): string | null => {
    // Check all rows are complete
    for (const row of rows) {
      if (!row.productTypeId || !row.countryId || !row.employmentTypeId || !row.employeeCount) {
        return t('K_VALIDATION_COMMERCIAL_FIELDS_REQUIRED', 'All commercial term fields are required.');
      }
      const count = Number(row.employeeCount);
      if (!Number.isInteger(count) || count <= 0) {
        return t('K_VALIDATION_EMPLOYEE_COUNT_POSITIVE', 'Employee count must be a positive whole number.');
      }
    }
    // Check no duplicate productType + country + employmentType combo
    const combos = new Set<string>();
    for (const row of rows) {
      const key = `${row.productTypeId}::${row.countryId}::${row.employmentTypeId}`;
      if (combos.has(key)) {
        return t('K_VALIDATION_DUPLICATE_COMMERCIAL_TERM', 'Duplicate product type, country, and employment type combination found.');
      }
      combos.add(key);
    }
    // Check all product types from Step 1 are covered
    const coveredProductTypes = new Set(rows.map((r) => r.productTypeId));
    for (const ptId of selectedProductTypes) {
      if (!coveredProductTypes.has(ptId)) {
        return t('K_VALIDATION_ALL_PRODUCT_TYPES_NEED_TERMS', 'All product types selected in Step 1 must have at least one commercial term.');
      }
    }
    return null;
  }, [t]);

  const countryOptions = useMemo(
    () => countries.map((c) => ({ label: c.name, value: c.id })),
    [countries],
  );

  const productTypeOptions = useMemo(
    () => (productTypesData?.content ?? []).map((pt) => ({ label: pt.productType, value: pt.id })),
    [productTypesData],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TenantFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  // Subdomain auto-sync: derive from company name until the user types in the subdomain field.
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const organizationName = watch('organizationName');

  useEffect(() => {
    if (isEditMode || subdomainTouched) return;
    const next = slugifyToSubdomain(organizationName ?? '');
    // Skip writes that would only surface a noisy "required" error before the user
    // has typed enough company name to produce a usable slug. slugifyToSubdomain
    // already returns '' for results below MIN_LEN.
    if (!next || next === getValues('subdomain')) return;
    setValue('subdomain', next, { shouldValidate: true, shouldDirty: false });
  }, [organizationName, isEditMode, subdomainTouched, setValue, getValues]);

  // Debounced subdomain uniqueness check via RHF validate.
  // Each keystroke resolves the previous pending promise as valid (superseded),
  // then starts a new 500ms timer before hitting the API.
  const subdomainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingResolveRef = useRef<((v: string | true) => void) | null>(null);

  const validateSubdomainUnique = useCallback(
    (value: string): Promise<string | true> | true => {
      // Resolve any previous pending check as valid (superseded by new input)
      if (pendingResolveRef.current) {
        pendingResolveRef.current(true);
        pendingResolveRef.current = null;
      }
      if (subdomainTimerRef.current) {
        clearTimeout(subdomainTimerRef.current);
      }

      // Skip for edit mode or values that fail other rules (those rules handle the error)
      if (isEditMode || !value || value.length < 3 || !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(value)) {
        return true;
      }

      return new Promise<string | true>((resolve) => {
        pendingResolveRef.current = resolve;
        subdomainTimerRef.current = setTimeout(async () => {
          try {
            const result = await checkSubdomain(value);
            resolve(
              result.available
                ? true
                : (result.message ?? t('K_VALIDATION_SUBDOMAIN_TAKEN', 'Subdomain is already taken')),
            );
          } catch {
            // Network error — allow through, server will reject on create
            resolve(true);
          }
          pendingResolveRef.current = null;
        }, 500);
      });
    },
    [isEditMode, t],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (subdomainTimerRef.current) {
        clearTimeout(subdomainTimerRef.current);
      }
      if (pendingResolveRef.current) {
        pendingResolveRef.current(true);
      }
    };
  }, []);

  // Step 0 → 1: Single filter call — fetch ALL matching forms (no attributes filter)
  const fetchResolvedTabs = useCallback(async (): Promise<boolean> => {
    const { country, productTypes } = getValues();
    setFilterLoading(true);
    setFilterError(null);
    // formDataMap and requiredFieldsMap intentionally NOT reset —
    // persisted data is restored for forms that survive the filter change

    try {
      // Fetch resolved countries + employment types per product type (for commercial terms)
      if (productTypes.length > 0) {
        const resolutions = await resolveCountries(productTypes);
        setCountryResolutions(resolutions);
      }

      let template;
      try {
        template = await getFormTemplateByScope(orgType, orgId, 'TENANT');
      } catch {
        // 404 — no template exists yet
        setResolvedTabs([]);
        setFilterLoading(false);
        return true;
      }

      const resolved = await filterFormTemplate(template.id, {
        filters: {
          ...(country ? { country: [country] } : {}),
          ...(productTypes.length > 0 ? { productType: productTypes } : {}),
        },
      });

      setResolvedTabs(resolved.tabs);
      setFilterLoading(false);
      return true;
    } catch {
      setFilterError(t('K_FAILED_LOAD_FORMS', 'Failed to load forms. Please try again.'));
      setFilterLoading(false);
      return false;
    }
  }, [getValues, orgType, orgId, t]);

  const stepper = useMultiStepForm({
    totalSteps: STEPS.length,
    onValidateStep: async (stepIndex) => {
      const fields = STEP_FIELDS[stepIndex];
      if (fields && fields.length > 0) {
        const isValid = await trigger(fields);
        if (!isValid) return false;
      }

      // Step 0 → 1: block if subdomain has a validation error, then fetch resolved tabs
      if (stepIndex === 0) {
        if (errors.subdomain) return false;
        return fetchResolvedTabs();
      }

      // Step 1 → 2: validate commercial terms
      if (stepIndex === 1) {
        const selectedPTs = getValues('productTypes');
        const error = validateCommercialTerms(commercialTermRows, selectedPTs);
        if (error) {
          setCommercialTermErrors(error);
          return false;
        }
        return true;
      }

      return true;
    },
  });

  // Client-side: filter mandatory forms from resolvedTabs
  const mandatoryForms = useMemo(() => {
    return resolvedTabs.flatMap((tab) =>
      tab.forms.filter((f) => f.attributes.isMandatory === true),
    );
  }, [resolvedTabs]);

  const mandatoryFormIds = useMemo(() => {
    return mandatoryForms.map((f) => f.formConfigId);
  }, [mandatoryForms]);

  const allMandatoryFormsFilled = useMemo(() => {
    if (mandatoryFormIds.length === 0) return true;
    return mandatoryFormIds.every(
      (id) => formDataMap[id] !== undefined || requiredFieldsMap[id] === false,
    );
  }, [mandatoryFormIds, formDataMap, requiredFieldsMap]);

  // Client-side: filter default forms (with their tabs) for Step 3
  const defaultTabs = useMemo(() => {
    return resolvedTabs
      .map((tab) => ({
        ...tab,
        forms: tab.forms.filter((f) => f.attributes.isDefault === true),
      }))
      .filter((tab) => tab.forms.length > 0);
  }, [resolvedTabs]);

  // Populate form when editing — keyed on modal open + editData ID
  const editTenantId = editData?.tenant.id;

  useEffect(() => {
    if (!isOpen) return;

    stepper.reset();
    setResolvedTabs([]);
    setFilterError(null);
    setFormDataMap({});
    setRequiredFieldsMap({});
    setSelectedModuleIds(new Set());
    setCommercialTermRows([]);
    setCommercialTermErrors(null);
    setCountryResolutions([]);

    if (editData) {
      reset({
        organizationName: editData.tenant.name,
        subdomain: editData.tenant.subdomain,
        organizationSubType: editData.organization?.organizationSubType ?? '',
        country: editData.tenant.country?.id ?? '',
        mobile: editData.user?.mobile ?? '',
        productTypes: editData.tenant.productTypeIds ?? [],
        firstName: editData.user?.firstName ?? '',
        lastName: editData.user?.lastName ?? '',
        email: editData.user?.email ?? '',
      });
      setSubdomainTouched(true);
    } else {
      reset(DEFAULT_FORM_VALUES);
      setSubdomainTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editTenantId]);

  const createMutation = useMutation({
    mutationFn: createTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      reset();
      stepper.reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: useCallback(
      (data: UpdateTenantPayload) => updateTenant(editData!.tenant.id, data),
      [editData],
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      reset();
      stepper.reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const handleFormSave = useCallback(
    (formConfigId: string, data: Record<string, unknown>) => {
      setFormDataMap((prev) => ({ ...prev, [formConfigId]: data }));
    },
    [],
  );

  const handleRequiredCheck = useCallback(
    (formConfigId: string, hasRequired: boolean) => {
      setRequiredFieldsMap((prev) => ({ ...prev, [formConfigId]: hasRequired }));
    },
    [],
  );

  /** Returns formDataMap entries that exist in the current resolvedTabs */
  const getRelevantFormData = useCallback((): Record<string, Record<string, unknown>> | undefined => {
    const currentFormIds = new Set(
      resolvedTabs.flatMap((tab) => tab.forms.map((f) => f.formConfigId)),
    );
    const filtered: Record<string, Record<string, unknown>> = {};
    for (const [id, data] of Object.entries(formDataMap)) {
      if (currentFormIds.has(id)) {
        filtered[id] = data;
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : undefined;
  }, [resolvedTabs, formDataMap]);

  const onSubmit = useCallback(
    async (data: TenantFormValues) => {
      if (isEditMode) {
        const payload: UpdateTenantPayload = {
          tenant: {
            name: data.organizationName,
            countryId: data.country || undefined,
            productTypeIds: data.productTypes,
            organizationSubType: (data.organizationSubType as CreateTenantPayload['tenant']['organizationSubType']) || undefined,
          },
          user: {
            firstName: data.firstName,
            lastName: data.lastName || undefined,
            mobile: data.mobile || undefined,
          },
        };
        updateMutation.mutate(payload);
      } else {
        const payload: CreateTenantPayload = {
          tenant: {
            name: data.organizationName,
            subdomain: data.subdomain,
            countryId: data.country,
            productTypeIds: data.productTypes,
            organizationSubType: (data.organizationSubType as CreateTenantPayload['tenant']['organizationSubType']) || undefined,
          },
          user: {
            firstName: data.firstName,
            lastName: data.lastName || undefined,
            email: data.email,
            mobile: data.mobile || undefined,
          },
          tabs: resolvedTabs.length > 0 ? resolvedTabs : undefined,
          formData: getRelevantFormData(),
          moduleIds: selectedModuleIds.size > 0 ? Array.from(selectedModuleIds) : undefined,
          commercialTerms: commercialTermRows.length > 0
            ? commercialTermRows.map((r) => ({
                countryId: r.countryId,
                productTypeId: r.productTypeId,
                employmentTypeId: r.employmentTypeId,
                employeeCount: Number(r.employeeCount),
              }))
            : undefined,
        };
        createMutation.mutate(payload);
      }
    },
    [isEditMode, createMutation, updateMutation, resolvedTabs, getRelevantFormData, selectedModuleIds, commercialTermRows],
  );

  const handleClose = useCallback(() => {
    if (!mutation.isPending) {
      reset();
      stepper.reset();
      setResolvedTabs([]);
      setFilterError(null);
      setFormDataMap({});
      setRequiredFieldsMap({});
      setSelectedModuleIds(new Set());
      setCommercialTermRows([]);
      setCommercialTermErrors(null);
      setCountryResolutions([]);
      onClose();
    }
  }, [mutation.isPending, reset, stepper, onClose]);

  const handleNext = useCallback(async () => {
    if (stepper.isLastStep) {
      handleSubmit(onSubmit)();
    } else {
      await stepper.goToNext();
    }
  }, [stepper, handleSubmit, onSubmit]);

  // Toggle isDefault for a specific form within a tab (Step 3)
  // Updates the full resolvedTabs so the save payload includes the change
  const handleFormDefaultToggle = useCallback(
    (tabIndex: number, formIndex: number) => {
      setResolvedTabs((prev) =>
        prev.map((tab, ti) => {
          if (ti !== tabIndex) return tab;
          return {
            ...tab,
            forms: tab.forms.map((form, fi) => {
              if (fi !== formIndex) return form;
              return { ...form, attributes: { ...form.attributes, isDefault: !form.attributes.isDefault } };
            }),
          };
        }),
      );
    },
    [],
  );

  // Disable Next on Step 1 if mandatory forms exist but aren't all filled
  const disableNextOnStep1 = stepper.currentStep === 1 && mandatoryFormIds.length > 0 && !allMandatoryFormsFilled;

  const footer = useMemo(
    () => (
      <StepperModalFooter
        isFirstStep={stepper.isFirstStep}
        isLastStep={stepper.isLastStep}
        onNext={handleNext}
        onPrev={stepper.goToPrev}
        onCancel={handleClose}
        isSubmitting={mutation.isPending || filterLoading}
        submitLabel={isEditMode ? t('K_UPDATE', 'Update') : t('K_SAVE', 'Save')}
        disableNext={disableNextOnStep1}
      />
    ),
    [stepper, handleNext, handleClose, mutation.isPending, filterLoading, isEditMode, disableNextOnStep1],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? t('K_MODAL_EDIT_TENANT', 'Edit Client') : t('K_MODAL_CREATE_TENANT', 'Create Client')}
      size="xl"
      footer={footer}
    >
      <Stepper
        steps={STEPS}
        activeStep={stepper.currentStep}
        onStepClick={stepper.goToStep}
      />

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.stepContainer}>
          {/* Step 1: Company Details */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 0 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_ORGANISATION_NAME', 'Company Name')}
                isRequired
                placeholder={t('K_PLACEHOLDER_ORGANISATION_NAME', 'Enter company name')}
                error={errors.organizationName?.message}
                {...register('organizationName', {
                  required: t('K_VALIDATION_ORGANISATION_NAME_REQUIRED', 'Company name is required'),
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
              <SubdomainField
                error={errors.subdomain?.message}
                disabled={isEditMode}
                helperText={
                  !isEditMode && !subdomainTouched && organizationName
                    ? t('K_HELPER_SUBDOMAIN_AUTO', 'Auto-generated from company name. Edit to customise.')
                    : undefined
                }
                {...register('subdomain', {
                  required: t('K_VALIDATION_SUBDOMAIN_REQUIRED', 'Subdomain is required'),
                  minLength: { value: 3, message: t('K_VALIDATION_MIN_3', 'Min 3 characters') },
                  maxLength: { value: 63, message: t('K_VALIDATION_MAX_63', 'Max 63 characters') },
                  pattern: {
                    value: /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
                    message: t('K_VALIDATION_SUBDOMAIN_PATTERN', 'Lowercase letters, numbers, and hyphens only'),
                  },
                  validate: validateSubdomainUnique,
                  onChange: () => setSubdomainTouched(true),
                })}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="organizationSubType"
                control={control}
                rules={{ required: t('K_VALIDATION_ORGANISATION_TYPE_REQUIRED', 'Company type is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_ORGANISATION_TYPE', 'Company Type')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_ORGANISATION_TYPE', 'Select company type')}
                    options={ORGANIZATION_SUB_TYPE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.organizationSubType?.message}
                  />
                )}
              />
              <Controller
                name="country"
                control={control}
                rules={{ required: t('K_VALIDATION_COUNTRY_REQUIRED', 'Country is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_COUNTRY', 'Country')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_COUNTRY', 'Select country')}
                    options={countryOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.country?.message}
                    loading={countriesLoading}
                    searchable
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="productTypes"
                control={control}
                rules={{
                  validate: (v) => v.length > 0 || t('K_VALIDATION_PRODUCT_TYPES_REQUIRED', 'At least one product type is required'),
                }}
                render={({ field }) => (
                  <MultiSelect
                    label={t('K_LABEL_PRODUCT_TYPES', 'Product Types')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_PRODUCT_TYPES', 'Select product types')}
                    options={productTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.productTypes?.message}
                  />
                )}
              />
            </div>

            <hr className={styles.separator} />

            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_FIRST_NAME', 'First Name')}
                isRequired
                placeholder={t('K_PLACEHOLDER_FIRST_NAME', 'Enter first name')}
                error={errors.firstName?.message}
                {...register('firstName', {
                  required: t('K_VALIDATION_FIRST_NAME_REQUIRED', 'First name is required'),
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
              <TextInput
                label={t('K_LABEL_LAST_NAME', 'Last Name')}
                isRequired
                placeholder={t('K_PLACEHOLDER_LAST_NAME', 'Enter last name')}
                error={errors.lastName?.message}
                {...register('lastName', {
                  required: t('K_VALIDATION_LAST_NAME_REQUIRED', 'Last name is required'),
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
            </div>

            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_EMAIL', 'Email')}
                isRequired
                type="email"
                placeholder={t('K_PLACEHOLDER_EMAIL', 'Enter email')}
                disabled={isEditMode}
                error={errors.email?.message}
                {...register('email', {
                  required: t('K_VALIDATION_EMAIL_REQUIRED', 'Email is required'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('K_VALIDATION_EMAIL_PATTERN', 'Enter a valid email address'),
                  },
                })}
              />
              <TextInput
                label={t('K_LABEL_MOBILE', 'Mobile')}
                placeholder="+254712345678"
                error={errors.mobile?.message}
                {...register('mobile', {
                  pattern: {
                    value: /^\+?[0-9]{7,15}$/,
                    message: t('K_VALIDATION_PHONE_PATTERN', 'Enter a valid phone number (7-15 digits)'),
                  },
                })}
              />
            </div>
          </div>

          {/* Step 2: Module Registry + Mandatory Forms */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 1 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            {/* Module Registry Accordion */}
            {!isEditMode && (defaultModules.length > 0 || optionalModules.length > 0) && (
              <div className={styles.tabAccordions}>
                <Accordion title={t('K_MODULE_REGISTRY', 'Module Registry')} variant="filled" defaultExpanded>
                  <div className={styles.moduleSection}>
                    {defaultModules.length > 0 && (
                      <>
                        <h4 className={styles.moduleSectionTitle}>{t('K_DEFAULT_MODULES', 'Default Modules')}</h4>
                        <div className={styles.moduleList}>
                          {defaultModules.map((mod) => (
                            <label key={mod.id} className={styles.formCheckboxItem}>
                              <input
                                type="checkbox"
                                checked
                                disabled
                                className={styles.formCheckbox}
                              />
                              <div className={styles.moduleInfo}>
                                <span className={styles.formCheckboxLabel}>{mod.name}</span>
                                {mod.description && (
                                  <span className={styles.moduleDesc}>{mod.description}</span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                    {optionalModules.length > 0 && (
                      <>
                        <h4 className={styles.moduleSectionTitle}>{t('K_OPTIONAL_MODULES', 'Optional Modules')}</h4>
                        <div className={styles.moduleList}>
                          {optionalModules.map((mod) => (
                            <label key={mod.id} className={styles.formCheckboxItem}>
                              <input
                                type="checkbox"
                                checked={selectedModuleIds.has(mod.id)}
                                onChange={() => toggleModule(mod.id)}
                                className={styles.formCheckbox}
                              />
                              <div className={styles.moduleInfo}>
                                <span className={styles.formCheckboxLabel}>{mod.name}</span>
                                {mod.description && (
                                  <span className={styles.moduleDesc}>{mod.description}</span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </Accordion>
              </div>
            )}

            {/* Commercial Terms Accordion */}
            {!isEditMode && (
              <div className={styles.tabAccordions}>
                <Accordion title={t('K_COMMERCIAL_TERMS', 'Commercial Terms')} variant="filled" defaultExpanded className={styles.commercialTermsAccordion}>
                  <div className={styles.commercialTermsSection}>
                    {commercialTermRows.map((row) => {
                      // Only product types selected in Step 1
                      const selectedPTIds = getValues('productTypes');
                      const ptOptions = (productTypesData?.content ?? [])
                        .filter((pt) => selectedPTIds.includes(pt.id))
                        .map((pt) => ({ label: pt.productType, value: pt.id }));

                      // Find resolution for this row's product type
                      const resolution = countryResolutions.find(
                        (r) => r.productTypeId === row.productTypeId,
                      );

                      // Countries: ALL → all countries from store; LEGAL_ENTITY → resolved list
                      const baseCountryOptions =
                        resolution?.resolutionType === 'ALL'
                          ? countryOptions
                          : resolution?.countries
                            ? countryOptions.filter((c) =>
                                resolution.countries!.some((rc) => rc.countryId === c.value),
                              )
                            : [];

                      // Employment types: ALL → all employment types; LEGAL_ENTITY → per-country
                      const baseEmploymentTypeOptions =
                        !row.productTypeId || !row.countryId
                          ? []
                          : resolution?.resolutionType === 'ALL'
                            ? employmentTypeOptions
                            : (() => {
                                const countryEntry = resolution?.countries?.find(
                                  (c) => c.countryId === row.countryId,
                                );
                                if (!countryEntry) return [];
                                return employmentTypeOptions.filter((et) =>
                                  countryEntry.employmentTypeIds.includes(et.value),
                                );
                              })();

                      // Hide employment types already chosen in sibling rows with the
                      // same product type + country combo (keep current row's selection visible)
                      const takenEmploymentTypeIds = new Set(
                        commercialTermRows
                          .filter(
                            (r) =>
                              r._id !== row._id &&
                              r.productTypeId === row.productTypeId &&
                              r.countryId === row.countryId &&
                              r.employmentTypeId,
                          )
                          .map((r) => r.employmentTypeId),
                      );
                      const rowEmploymentTypeOptions = baseEmploymentTypeOptions.filter(
                        (et) => !takenEmploymentTypeIds.has(et.value),
                      );

                      return (
                        <div key={row._id} className={styles.commercialTermRow}>
                          <Select
                            label={t('K_LABEL_PRODUCT_TYPE', 'Product Type')}
                            isRequired
                            placeholder={t('K_PLACEHOLDER_PRODUCT_TYPE', 'Select product type')}
                            options={ptOptions}
                            value={row.productTypeId}
                            onChange={(v: string) => updateCommercialTermRow(row._id, { productTypeId: v, countryId: '', employmentTypeId: '', employeeCount: '' })}
                            fullWidth
                          />
                          <Select
                            label={t('K_LABEL_COUNTRY', 'Country')}
                            isRequired
                            placeholder={t('K_PLACEHOLDER_COUNTRY', 'Select country')}
                            options={baseCountryOptions}
                            value={row.countryId}
                            onChange={(v: string) => updateCommercialTermRow(row._id, { countryId: v, employmentTypeId: '', employeeCount: '' })}
                            searchable
                            fullWidth
                          />
                          <Select
                            label={t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}
                            isRequired
                            placeholder={
                              !row.countryId
                                ? t('K_PLACEHOLDER_SELECT_COUNTRY_FIRST', 'Select a country first')
                                : t('K_PLACEHOLDER_EMPLOYMENT_TYPE', 'Select employment type')
                            }
                            options={rowEmploymentTypeOptions}
                            value={row.employmentTypeId}
                            onChange={(v: string) => updateCommercialTermRow(row._id, { employmentTypeId: v })}
                            disabled={!row.countryId}
                            fullWidth
                          />
                          <TextInput
                            label={t('K_LABEL_EMPLOYEE_COUNT', 'Employee Count')}
                            isRequired
                            type="number"
                            placeholder={t('K_PLACEHOLDER_EMPLOYEE_COUNT', 'Enter count')}
                            value={row.employeeCount}
                            onChange={(e) => updateCommercialTermRow(row._id, { employeeCount: e.target.value })}
                            fullWidth
                          />
                          <div className={styles.commercialTermActions}>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => removeCommercialTermRow(row._id)}
                            >
                              −
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    <div className={styles.commercialTermAddRow}>
                      <Button variant="outline" size="sm" onClick={addCommercialTermRow}>
                        {t('K_BUTTON_ADD', '+ Add')}
                      </Button>
                    </div>
                    {commercialTermErrors && (
                      <p className={styles.errorText}>{commercialTermErrors}</p>
                    )}
                  </div>
                </Accordion>
              </div>
            )}

            {/* Mandatory Forms */}
            {filterLoading && (
              <p className={styles.placeholderText}>{t('K_LOADING_FORMS', 'Loading forms...')}</p>
            )}

            {filterError && (
              <p className={styles.errorText}>{filterError}</p>
            )}

            {!filterLoading && !filterError && mandatoryForms.length === 0 && (
              <p className={styles.placeholderText}>
                {t('K_NO_MANDATORY_FORMS', 'No mandatory forms to fill.')}
              </p>
            )}

            {!filterLoading && !filterError && mandatoryForms.length > 0 && (
              <div className={styles.tabAccordions}>
                {mandatoryForms.map((form) => (
                  <MandatoryFormRenderer
                    key={form.formConfigId}
                    formConfigId={form.formConfigId}
                    formName={form.formName ?? form.formConfigId}
                    initialSaved={formDataMap[form.formConfigId] !== undefined}
                    initialData={formDataMap[form.formConfigId]}
                    onSave={handleFormSave}
                    onRequiredCheck={handleRequiredCheck}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Step 3: Form Templates — all resolvedTabs with isDefault checkboxes */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 2 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            {defaultTabs.length === 0 && (
              <p className={styles.placeholderText}>
                {t('K_NO_REQUESTED_DATA_FORMS', 'No forms available for requested data.')}
              </p>
            )}

            {defaultTabs.length > 0 && (
              <div className={styles.tabAccordions}>
                {resolvedTabs.map((tab, tabIndex) => (
                  <Accordion
                    key={tab.name + String(tabIndex)}
                    title={tab.name}
                    variant="filled"
                    defaultExpanded={tabIndex === 0}
                  >
                    <div className={styles.formCheckboxList}>
                      {tab.forms.map((form, formIndex) => (
                        <label
                          key={form.formConfigId + String(formIndex)}
                          className={styles.formCheckboxItem}
                        >
                          <input
                            type="checkbox"
                            checked={form.attributes.isDefault}
                            onChange={() => handleFormDefaultToggle(tabIndex, formIndex)}
                            className={styles.formCheckbox}
                          />
                          <span className={styles.formCheckboxLabel}>
                            {form.formName ?? form.formConfigId}
                          </span>
                        </label>
                      ))}
                      {tab.forms.length === 0 && (
                        <p className={styles.noFormsText}>{t('K_NO_FORMS_IN_TAB', 'No forms in this tab.')}</p>
                      )}
                    </div>
                  </Accordion>
                ))}
              </div>
            )}
          </div>
        </div>

      </form>
    </Modal>
  );
}

// ============================================
// SUBDOMAIN FIELD — Input with .workpay.com suffix
// ============================================

interface SubdomainFieldProps {
  error?: string;
  disabled?: boolean;
  helperText?: string;
  name: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
  ref: React.Ref<HTMLInputElement>;
}

function SubdomainField({ error, disabled, helperText, name, onChange, onBlur, ref }: SubdomainFieldProps) {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);

  const wrapperClasses = [
    styles.subdomainWrapper,
    focused && styles.subdomainFocused,
    error && styles.subdomainError,
    disabled && styles.subdomainDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    setFocused(false);
    onBlur(e);
  };

  return (
    <FormField id="subdomain" label={t('K_LABEL_SUBDOMAIN', 'Subdomain')} isRequired error={error} helperText={helperText}>
      <div className={wrapperClasses}>
        <input
          ref={ref}
          id="subdomain"
          name={name}
          type="text"
          className={styles.subdomainInput}
          placeholder="acme"
          disabled={disabled}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          aria-invalid={Boolean(error) || undefined}
          aria-required
        />
        <span className={styles.subdomainSuffix}>.workpay.com</span>
      </div>
    </FormField>
  );
}

export default TenantModal;
